import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Story 24.3 — the count badge (DESIGN.md › Components › Badges ›
 * badge-count). Roles only.
 *
 * - Fail-soft: nothing renders for 0, a negative number, `null`, `undefined`
 *   or `NaN` — an unknown count shows no badge, and there is no dot state.
 * - "9+" past nine.
 * - `aria-hidden`: the count is decoration. The control it sits on carries
 *   the number in its own accessible name ("Filter, 3 active").
 * - `ringClassName` adds the 2px ring in the parent's surface colour where
 *   the badge overlaps an icon or avatar (e.g. `ring-2 ring-surface`).
 */

type CountBadgeProps = Omit<React.ComponentProps<"span">, "children"> & {
  count?: number | null
  ringClassName?: string
}

function formatCount(count: number | null | undefined): string | null {
  if (count === null || count === undefined || !Number.isFinite(count) || count <= 0) return null
  return count > 9 ? "9+" : String(Math.floor(count))
}

function CountBadge({ count, ringClassName, className, ...props }: CountBadgeProps) {
  const label = formatCount(count)
  if (label === null) return null
  return (
    <span
      aria-hidden="true"
      data-slot="count-badge"
      className={cn(
        "inline-flex min-h-[18px] min-w-[18px] shrink-0 items-center justify-center rounded-full bg-badge px-1 type-badge-digits text-badge-ink forced-color-adjust-none",
        ringClassName,
        className
      )}
      {...props}
    >
      {label}
    </span>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export { CountBadge, formatCount }
